import { Card } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import type { ComponentProps } from 'react'
import { useMemo } from 'react'

type SFDateCardSize = 'xs' | 'sm' | 'md' | 'lg'

interface SFDateCardProps extends Omit<ComponentProps<'div'>, 'children'> {
  date: Date | string
  size?: SFDateCardSize
}

const sizeVariants: Record<
  SFDateCardSize,
  {
    card: string
    section: string
    daySection: string
    monthText: string
    dayText: string
    footerText: string
  }
> = {
  xs: {
    card: 'w-[50px]',
    section: 'p-0',
    daySection: 'p-0',
    monthText: 'text-xs',
    dayText: 'text-base',
    footerText: 'text-xs',
  },
  sm: {
    card: 'w-[60px]',
    section: 'p-0.5',
    daySection: 'p-0.5',
    monthText: 'text-xs',
    dayText: 'text-lg',
    footerText: 'text-xs',
  },
  md: {
    card: 'w-[80px]',
    section: 'p-1',
    daySection: 'p-1',
    monthText: 'text-sm',
    dayText: 'text-xl',
    footerText: 'text-sm',
  },
  lg: {
    card: 'w-[100px]',
    section: 'p-3',
    daySection: 'p-3',
    monthText: 'text-base',
    dayText: 'text-2xl',
    footerText: 'text-base',
  },
}

const SFDateCard = (props: SFDateCardProps) => {
  const { className, date, size = 'md', ...rest } = props
  const dateObj = useMemo(() => new Date(date), [date])
  const currentYear = new Date().getFullYear()
  const showYear = dateObj.getFullYear() !== currentYear

  // Get formatted date parts
  const month = dateObj.toLocaleString('default', { month: 'short' })
  const day = dateObj.getDate()
  const year = dateObj.getFullYear()
  const dayOfWeek = dateObj.toLocaleString('default', { weekday: 'short' })

  const variant = sizeVariants[size]

  return (
    <Card
      className={cn(
        'flex flex-col gap-0 overflow-hidden rounded-md border-1 border-muted p-0 py-0 text-white shadow-sm ring-0',
        variant.card,
        className
      )}
      {...rest}
    >
      <div className={cn('grid flex-1 place-items-center bg-primary text-center', variant.section)}>
        <span className={cn('block font-bold text-primary-foreground', variant.monthText)}>{month}</span>
      </div>
      <div className={cn('grid flex-1 place-items-center bg-card text-primary', variant.daySection)}>
        <span className={cn('block font-bold text-card-foreground', variant.dayText)}>{day}</span>
      </div>
      {showYear ? (
        <div className={cn('grid flex-1 place-items-center bg-primary text-center', variant.section)}>
          <span className={cn('block font-bold text-primary-foreground', variant.footerText)}>{year}</span>
        </div>
      ) : (
        <div className={cn('grid flex-1 place-items-center bg-primary text-center', variant.section)}>
          <span className={cn('block font-bold text-primary-foreground', variant.footerText)}>{dayOfWeek}</span>
        </div>
      )}
    </Card>
  )
}

export default SFDateCard
